import { makeHttpClient, _unwrapAxiosResponse } from "./http";
import env from "@/environment/env.json";
import { ImportType } from "@/constants/ImportType";
import {
  DialogProgrammatic as Dialog,
  ToastProgrammatic as Toast
} from "buefy";
export interface CustomerEntityResponse {
  id: number;
  name: string;
  address: string;
  entity_country: number;
}

export interface CustomerScope {
  country_of_activity: number;
  stream_in_scope: number;
}

export interface CustomerProfileResponse {
  id: number;
  name: string;
  address: string;
  merchant_id: string;
  crm_id: string;
  account_manager: number;
  reporting_responsible: number;
  raw_data_management: boolean;
  customer_reports_approval: boolean;
  compliance_cost_dashboard: boolean;
  compliance_cost_playground: boolean;
  data_delivery_deadline: string;
  custom_message: string;
  communication_language: number;
  contact_person: number;
  entities: Array<CustomerEntityResponse>;
  scope: Array<CustomerScope>;
  customer_type: string;
}

export interface CustomerReportResponse {
  id: number;
  customer: number;
  scheme: number;
  stream: number;
  reports: Array<number>;
  valid_from: Date;
  valid_to: Date;
  additional_info: string;
}

export interface LanguageResponse {
  id: number;
  name: string;
}

function dateFormatter(dt: any) {
  if (dt == "Invalid Date") {
    return null;
  }
  if (dt != null) {
    return dt.toLocaleDateString("fr-CA");
  }
  return dt;
}

export class CustomerProfileDataService {
  // private cacheClient = cachingHttpClient("customers", 300);
  private httpClient = makeHttpClient();

  getAll(query: string) {
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(
        env.customerUrl + "list/view/?" + query
      )
    );
  }
  getCustomerLocalEntityData(customerId: number) {
    let url = env.customerUrl + "list/?import=True&only_entities=True";
    if (customerId != null) {
      url = url + "&customer=" + customerId;
    }
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }
  getCustomerProfileAll() {
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(
        env.customerUrl + "profile/"
      )
    );
  }

  getLanguages() {
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<LanguageResponse>>(
        env.customerUrl + "language/"
      )
    );
  }

  create(form: any, entities: any) {
    const data = {
      name: form.name,
      name_short: form.name_short,
      address: form.address,
      crm_id: form.crm_id,
      merchant_id: form.merchant_id,
      company_hierarchy: form.company_hierarchy,
      account_manager: form.account_manager_id,
      backup_account_manager: form.backup_account_manager_id,
      reporting_responsible: form.reporting_responsible_id,
      raw_data_management: form.raw_data_management,
      customer_reports_approval: form.customer_reports_approval,
      compliance_cost_dashboard: form.compliance_cost_dashboard,
      compliance_cost_playground: form.compliance_cost_playground,
      data_delivery_deadline: form.data_delivery_deadline,
      custom_message: form.custom_message,
      communication_language: form.communication_language,
      entities: entities,
      proof_of_submission: form.proof_of_submission,
      landbell_entity: form.landbell_entity,
      proof_of_submission_countries: form.proof_of_submission_countries,
      sales_accountable: form.sales_accountable,
      operational: form.operational,
      rc_template_for_reminders: form.rc_template_for_reminders,
      customer_type: form.customer_type
    };
    return _unwrapAxiosResponse(
      this.httpClient.post<CustomerProfileResponse>(
        env.customerUrl + "profile/",
        data
      )
    );
  }

  update(form: any, entities: any) {
    const data = {
      id: form.id,
      name: form.name,
      name_short: form.name_short,
      address: form.address,
      crm_id: form.crm_id,
      merchant_id: form.merchant_id,
      company_hierarchy: form.company_hierarchy,
      account_manager: form.account_manager_id,
      backup_account_manager: form.backup_account_manager_id,
      reporting_responsible: form.reporting_responsible_id,
      raw_data_management: form.raw_data_management,
      customer_reports_approval: form.customer_reports_approval,
      compliance_cost_dashboard: form.compliance_cost_dashboard,
      compliance_cost_playground: form.compliance_cost_playground,
      data_delivery_deadline: form.data_delivery_deadline,
      custom_message: form.custom_message,
      communication_language: form.communication_language,
      entities: entities,
      proof_of_submission: form.proof_of_submission,
      landbell_entity: form.landbell_entity,
      proof_of_submission_countries: form.proof_of_submission_countries,
      sales_accountable: form.sales_accountable,
      operational: form.operational,
      rc_template_for_reminders: form.rc_template_for_reminders,
      customer_type: form.customer_type
    };
    return _unwrapAxiosResponse(
      this.httpClient.put<CustomerProfileResponse>(
        env.customerUrl + "profile/" + `${form.id}/`,
        data
      )
    );
  }

  exportCustomerInfo(
    query: any,
    ids: number[],
    isCheckedAll: boolean,
    sheetName: any
  ) {
    const params = new URLSearchParams();
    ids.map(id => {
      params.append("id", "" + id);
    });
    params.append("isCheckedAll", "" + isCheckedAll);
    params.append(
      "is_customer_information_sheet",
      "" + sheetName.is_customer_information_sheet
    );
    params.append("is_scope_sheet", "" + sheetName.is_scope_sheet);
    params.append(
      "is_client_credentials_sheet",
      "" + sheetName.is_client_credentials_sheet
    );
    params.append(
      "is_registration_numbers_sheet",
      "" + sheetName.is_registration_numbers_sheet
    );
    params.append(
      "is_reporting_categories_sheet",
      "" + sheetName.is_reporting_categories_sheet
    );
    const url = `${
      env.customerUrl
    }export-customer-profile/?${query}&${params.toString()}`;
    const successToast = Toast.open({
      indefinite: true,
      message: "Download in Progress!",
      type: "is-success"
    });
    this.httpClient
      .get(url, { responseType: "blob" })
      .then(({ data }) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "CustomerInformation.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        successToast.close();
      })
      .catch(error => {
        successToast.close();
        Toast.open({
          message: "Download failed. Please try again or contact support.",
          type: "is-danger"
        });
      });
  }

  getCustomerList(customerId: number, activeEntities = false) {
    let url = env.customerUrl + "list/";
    if (customerId != null) {
      url = url + "?customer=" + customerId;

      if (activeEntities == true) {
        url = url + "&active_entities=True";
      }
    }

    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }

  fetchCustomerList(customerId: number, activeEntities = false) {
    let url = env.customerUrl + "customer-list-myuploads/";
    if (customerId != null) {
      url = url + "?customer=" + customerId;

      if (activeEntities == true) {
        url = url + "&active_entities=True";
      }
    }

    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }

  getParentCustomerListReportRun(customerId: number) {
    let url =
      env.customerUrl +
      "list/?import=True&only_entities=False&active_entities=True&test_run=True";
    if (customerId != null) {
      url = url + "&customer=" + customerId;
    }
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }
  getParentCustomerListWithPublic(customerId: number) {
    let url =
      env.customerUrl + `import/list/?import_type=${ImportType.PRODUCTS}`;
    if (customerId != null) {
      url = url + "&customer=" + customerId;
    }
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }
  getParentCustomerList(customerId: number) {
    let url = env.customerUrl + "list/?import=True";
    if (customerId != null) {
      url = url + "&customer=" + customerId;
    }
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }
  getCustomerListForImport(import_type: string) {
    let url = env.customerUrl + "import/list/";
    if (import_type != null) {
      url = url + "?import_type=" + import_type;
    }
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }

  getCustomerById(id: any) {
    return _unwrapAxiosResponse(
      this.httpClient.get<CustomerProfileResponse>(
        env.customerUrl + "profile/" + `${id}/`
      )
    );
  }
  getCustomerReports(customerId: number) {
    let url;
    if (customerId != null) {
      url = env.customerUrl + "reports/?customer=" + customerId;
    } else {
      url = env.customerUrl + "reports/";
    }
    return _unwrapAxiosResponse(
      this.httpClient.get<Array<CustomerProfileResponse>>(url)
    );
  }

  createCustomerReport(report: any) {
    const data = {
      customer: report.customer,
      country: report.country_id,
      scheme: report.scheme_id,
      stream: report.stream_id,
      report: report.report,
      valid_from: dateFormatter(report.valid_from_date),
      valid_to: dateFormatter(report.valid_to_date),
      additional_info: report.additional_info
    };
    return _unwrapAxiosResponse(
      this.httpClient.post<CustomerReportResponse>(
        env.customerUrl + "reports/",
        data
      )
    );
  }

  updateCustomerReport(report: any) {
    const data = {
      customer: report.customer,
      country: report.country_id,
      scheme: report.scheme_id,
      stream: report.stream_id,
      report: report.report,
      valid_from: dateFormatter(report.valid_from_date),
      valid_to: dateFormatter(report.valid_to_date),
      additional_info: report.additional_info
    };
    return _unwrapAxiosResponse(
      this.httpClient.put<CustomerReportResponse>(
        env.customerUrl + "reports/" + `${report.id}/`,
        data
      )
    );
  }

  deactivate(id: number) {
    const data = {
      pk: id
    };
    return _unwrapAxiosResponse(
      this.httpClient.post<CustomerProfileResponse>(
        env.customerUrl + "profile/deactivate/",
        data
      )
    );
  }

  removeCustomerReport(id: number) {
    return _unwrapAxiosResponse(
      this.httpClient.delete<CustomerReportResponse>(
        env.customerUrl + "reports/" + `${id}/`
      )
    );
  }

  deactivateEntity(ids: number[]) {
    const data = {
      entityIds: ids
    };
    return _unwrapAxiosResponse(
      this.httpClient.post<CustomerProfileResponse>(
        env.customerUrl + "profile/deactivate_entity/",
        data
      )
    );
  }

  exportCustomerScope(id: number) {
    const url = env.customerUrl + "export/scope/?customer_id=" + id;

    this.httpClient
      .get(url, { responseType: "blob" })
      .then(({ data }) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "customer_scope_data.xlsx"); //any other extension
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(err => {
        Dialog.alert({
          title: "Export not possible",
          message:
            "There was an error exporting customer scope data. Please contact your Customer Account Manager.",
          type: "is-danger",
          ariaRole: "alertdialog",
          ariaModal: true
        });
        return err;
      });
  }
  downloadUserManual(id: number, userType: any) {
    const url =
      env.customerUrl +
      "user/manual/?customer_id=" +
      id +
      "&userType=" +
      userType;
    this.httpClient
      .get(url, { responseType: "blob" })
      .then(({ data }) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = downloadUrl;
        if (userType == "internal") {
          link.setAttribute("download", "Circul8 EPR Manual Internal Use.pdf");
        } else {
          link.setAttribute(
            "download",
            "Circul8 EPR Manual for EXTERNAL USERS_UPDATED_version 3.0.pdf"
          );
        }
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(err => {
        Dialog.alert({
          title: "Download not possible",
          message:
            "There was an error downloading the user manual. Please contact your Customer Account Manager.",
          type: "is-danger",
          ariaRole: "alertdialog",
          ariaModal: true
        });
        return err;
      });
  }
}

export default CustomerProfileDataService;
